/**
 * theme.ts — the dark/light theme store and the PORTAL RULE.
 *
 * ── The portal rule (this file is the whole of it) ─────────────────────────
 * `data-theme` is stamped on **document.documentElement**, and
 * src/styles/klaxiora.css binds every token AND `color-scheme` at
 * `:root[data-theme=…]`. Because the attribute sits on the root, EVERY subtree
 * inherits both — including the drawers and modals that render through
 * createPortal into document.body, outside the shell.
 *
 * That is what replaced the old swept recipe (an inline `colorScheme:'dark'`
 * on every portal surface, 13 of them). The recipe was correct for a
 * dark-only app and becomes actively WRONG the moment a light theme exists:
 * an inline 'dark' would keep rendering native selects, date pickers and
 * scrollbars for a dark ground inside a light drawer — the exact
 * inverted-legibility bug the recipe was written to fix, resurrected.
 * A grep fence in src/theme/no-raw-hex.test.ts bans the declaration from
 * coming back, so the bug is structurally impossible rather than remembered.
 *
 * ── Mission Control ────────────────────────────────────────────────────────
 * MC is unmigrated (its own arc). In mission-control mode `clearTheme()`
 * REMOVES the attribute, so not one rule in klaxiora.css matches and MC
 * renders exactly as it did before this slice — including its own `:root`
 * tokens in styles/tokens.css, which share the names `--accent` and
 * `--danger` with the design system.
 *
 * ── Persistence ────────────────────────────────────────────────────────────
 * localStorage `gss.theme`, following the `gss.appMode` precedent. DARK is
 * the default, so an existing user's first load looks like it always did.
 * No backend: /v1/settings is tenant-scoped, the wrong scope for a personal
 * preference. index.html carries a pre-paint copy of the read+stamp so the
 * correct ground is painted before React mounts (no flash).
 */
import { useSyncExternalStore } from 'react'

export type Theme = 'dark' | 'light'

export const THEME_KEY = 'gss.theme'
export const APP_MODE_KEY = 'gss.appMode'

const listeners = new Set<() => void>()

function readStored(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark' // storage unavailable — non-fatal, dark is the default
  }
}

let current: Theme = readStored()

export function getTheme(): Theme {
  return current
}

/**
 * Stamp the theme on <html> and repaint the two grounds.
 *
 * The grounds are read back OUT of the stylesheet (`--bg`) rather than
 * duplicated here, so klaxiora.css stays the only place a colour is written.
 * `document.body.style.background` is the design system's own instruction
 * (README "Light theme"): without it the overscroll area shows the wrong
 * ground when a page is rubber-banded.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  root.setAttribute('data-theme', theme)

  const ground = getComputedStyle(root).getPropertyValue('--bg').trim()
  if (!ground) return // stylesheet not parsed yet — the pre-paint script covered it
  root.style.background = ground
  document.body.style.background = ground
  const meta = document.querySelector('meta[name="theme-color"]')
  meta?.setAttribute('content', ground)
}

/**
 * Mission-control mode: no attribute, so klaxiora.css does not apply at all.
 * The inline grounds are removed too, handing the page back to index.html's
 * static FOUC rule and MC's own stylesheets.
 */
export function clearTheme(): void {
  const root = document.documentElement
  root.removeAttribute('data-theme')
  root.style.removeProperty('background')
  document.body.style.removeProperty('background')
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta?.dataset['default']) meta.setAttribute('content', meta.dataset['default'])
}

export function setTheme(theme: Theme): void {
  current = theme
  try {
    localStorage.setItem(THEME_KEY, theme)
  } catch {
    /* storage unavailable — the theme still applies for this session */
  }
  applyTheme(theme)
  for (const l of listeners) l()
}

export function toggleTheme(): void {
  setTheme(current === 'dark' ? 'light' : 'dark')
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

/** Re-renders the caller whenever the theme changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme, getTheme)
}
